/**
 * Visit packets (Phase 16A, design §6.5): minimum-necessary data for the clinician's assigned
 * patients with shifts in the next 36 h, or a visit in the last 48 h whose chart is still open.
 * Downloaded while online and kept only in the encrypted offline store.
 */
import { apiClient } from '../api-client';
import type { OfflinePacketsResponse, PatientPacketDto } from '../../dtos/offline/offline.dto';
import type { PacketShiftDto, PacketTemplateDto, PacketVisitDto } from '../../dtos/offline/offline.dto';
import type { EscrowKeyDto } from '../../dtos/offline/sync.dto';
import { OfflineSession, OfflineSessionImpl } from './offlineSession';
import { isOfflineDisabledError, OFFLINE_DISABLED_MESSAGE } from './network';

const PACKETS_ENDPOINT = '/patientmr/me/offline-packets';
const SYNC_META_KEY = 'packetsSyncedAt';
/** Same meta key the outbox seals with (not PHI: a public key) */
export const ESCROW_META_KEY = 'escrowKey';
const LATE_ENTRY_META_KEY = 'lateEntryMinutes';
const TEMPLATES_META_KEY = 'templates';
export const DEFAULT_LATE_ENTRY_MINUTES = 60;

/** 16D kill switch: packets refused platform-wide (queued work still syncs). */
export class OfflineDisabledError extends Error {
  constructor() {
    super(OFFLINE_DISABLED_MESSAGE);
    this.name = 'OfflineDisabledError';
  }
}

/** What the offline visit workspace needs to start or chart a visit from a packet. */
export interface PacketVisitContext {
  packet: PatientPacketDto;
  shift: PacketShiftDto | null;
  postVisit: PacketVisitDto | null;
  evvRequired: boolean;
}
export const PACKETS_CHANGED_EVENT = 'offline-packets-changed';

export interface PacketSyncResult {
  patients: number;
  syncedAt: string;
}

const notify = () => { if (typeof window !== 'undefined') window.dispatchEvent(new Event(PACKETS_CHANGED_EVENT)); };

export class PacketService {
  constructor(private readonly session: OfflineSessionImpl = OfflineSession) {}

  private store() {
    const store = this.session.getStore();
    if (!store) throw new Error('Offline store is locked');
    return store;
  }

  /**
   * Download packets and replace the stored set (patients no longer assigned are dropped).
   * Throws when offline, locked, or the device is not enrolled.
   */
  async refresh(): Promise<PacketSyncResult> {
    const profile = this.session.getProfile();
    if (!profile) throw new Error('Offline mode is not set up on this device');
    const store = this.store();
    let response;
    try {
      response = await apiClient.get<OfflinePacketsResponse>(PACKETS_ENDPOINT, undefined, { headers: { 'X-Device-Id': profile.deviceId } });
    } catch (e) {
      if (isOfflineDisabledError(e)) throw new OfflineDisabledError();
      throw e;
    }
    const patients = response.data?.patients || [];
    const keep = new Set(patients.map((p) => p.patientId));
    for (const { key } of await store.getAll<PatientPacketDto>('packets')) {
      if (!keep.has(key)) await store.delete('packets', key);
    }
    for (const packet of patients) await store.put('packets', packet.patientId, packet);
    const syncedAt = response.data?.generatedAt || new Date().toISOString();
    await store.putMeta(SYNC_META_KEY, syncedAt);
    // 16C (16B B12): tenant escrow key, late-entry threshold and templates; none of them PHI
    if (response.data?.escrow?.kid && response.data.escrow.spki) await store.putMeta(ESCROW_META_KEY, response.data.escrow);
    if (typeof response.data?.lateEntryMinutes === 'number') await store.putMeta(LATE_ENTRY_META_KEY, response.data.lateEntryMinutes);
    if (Array.isArray(response.data?.templates)) await store.putMeta(TEMPLATES_META_KEY, response.data.templates);
    notify();
    return { patients: patients.length, syncedAt };
  }

  /** Unexpired packets, ordered by the next shift start. Expired ones are deleted. */
  async list(nowMs: number = Date.now()): Promise<PatientPacketDto[]> {
    const store = this.store();
    const live: PatientPacketDto[] = [];
    for (const { key, value } of await store.getAll<PatientPacketDto>('packets')) {
      if (Date.parse(value.expiresAt) <= nowMs) await store.delete('packets', key);
      else live.push(value);
    }
    const nextStart = (p: PatientPacketDto) => p.shifts.map((s) => s.start).sort()[0] || '9999';
    return live.sort((a, b) => nextStart(a).localeCompare(nextStart(b)));
  }

  async get(patientId: string): Promise<PatientPacketDto | null> {
    const packet = await this.store().get<PatientPacketDto>('packets', patientId);
    return packet && Date.parse(packet.expiresAt) > Date.now() ? packet : null;
  }

  /** The packet plus the shift (or post-visit) being worked; null when not on this device. */
  async getVisitContext(patientId: string, scheduleId?: string | null, visitId?: string | null): Promise<PacketVisitContext | null> {
    const packet = await this.get(patientId);
    if (!packet) return null;
    const shift = (scheduleId && packet.shifts.find((s) => s.shiftId === scheduleId)) || null;
    const postVisit = (visitId && packet.recentVisits.find((v) => v.visitId === visitId)) || null;
    return { packet, shift, postVisit, evvRequired: Boolean(shift?.evvRequired ?? packet.evvRequired) };
  }

  async getEscrowKey(): Promise<EscrowKeyDto | null> {
    return (await this.store().getMeta<EscrowKeyDto>(ESCROW_META_KEY)) || null;
  }

  async lateEntryMinutes(): Promise<number> {
    return (await this.store().getMeta<number>(LATE_ENTRY_META_KEY)) ?? DEFAULT_LATE_ENTRY_MINUTES;
  }

  async templates(): Promise<PacketTemplateDto[]> {
    return (await this.store().getMeta<PacketTemplateDto[]>(TEMPLATES_META_KEY)) || [];
  }

  async lastSyncedAt(): Promise<string | null> {
    return (await this.store().getMeta<string>(SYNC_META_KEY)) || null;
  }
}

export const Packets = new PacketService();

export default Packets;
